import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import './ArtifactPage.css';
import { loadArtifact, submitArtifactEvent } from './artifactApiClient';
import ArtifactStatusScreen from './ArtifactStatusScreen';
import ArtifactRenderer from '../../Artifacts/ArtifactRenderer';

function statusFromArtifact(artifact) {
  const status = String(artifact?.status || '').toUpperCase();
  if (status === 'EXPIRED') return 'expired';
  if (status === 'REVOKED') return 'revoked';
  if (status === 'COMPLETED') return 'already_completed';
  if (status === 'DISABLED') return 'disabled';
  return null;
}

export default function ArtifactPage() {
  const { token } = useParams();
  const [artifact, setArtifact] = useState(null);
  const [status, setStatus] = useState('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [submittingEvent, setSubmittingEvent] = useState('');

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    loadArtifact(token)
      .then(data => {
        if (cancelled) return;
        setArtifact(data);
        setStatus(statusFromArtifact(data) || 'ready');
      })
      .catch(error => {
        if (cancelled) return;
        const responseStatus = error?.response?.status;
        if (responseStatus === 401) setStatus('auth_required');
        else if (responseStatus === 403) setStatus('unauthorized');
        else if (responseStatus === 404) setStatus('not_found');
        else if (responseStatus === 410) setStatus('expired');
        else {
          setStatus('load_failed');
          setErrorMessage(
            error?.response?.data?.error || 'Unable to load this artifact.'
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmitEvent = useCallback(
    async (eventName, payload) => {
      if (!eventName) {
        setStatus('submit_failed');
        setErrorMessage('This artifact form is missing an event name.');
        return;
      }
      setSubmittingEvent(eventName);
      setErrorMessage('');
      try {
        const response = await submitArtifactEvent(token, eventName, payload);
        setArtifact(current => ({
          ...current,
          status: response?.status || current?.status,
          submitted: response?.completed || current?.submitted,
        }));
        setStatus(response?.completed ? 'submitted' : 'ready');
      } catch (error) {
        setStatus('submit_failed');
        setErrorMessage(
          error?.response?.data?.error || 'Unable to submit this artifact.'
        );
      } finally {
        setSubmittingEvent('');
      }
    },
    [token]
  );

  if (status === 'loading') {
    return <ArtifactStatusScreen status="loading" />;
  }

  if (status !== 'ready') {
    return <ArtifactStatusScreen status={status} message={errorMessage} />;
  }

  return (
    <main className="artifact-page">
      <section className="artifact-viewer-shell">
        <header className="artifact-viewer-header">
          <p className="artifact-eyebrow">Workflow86 Artifact</p>
          <h1>{artifact?.title || 'Artifact'}</h1>
          {artifact?.description ? <p>{artifact.description}</p> : null}
        </header>

        <ArtifactRenderer
          artifact={artifact}
          className="artifact-html-surface"
          onSubmitEvent={handleSubmitEvent}
          title={artifact?.title || 'Artifact'}
          minHeight={160}
        />

        {submittingEvent ? (
          <div className="artifact-submit-banner" role="status">
            Submitting {submittingEvent}...
          </div>
        ) : null}
      </section>
    </main>
  );
}
